// Forgot Password Page Component
// Implements password reset functionality using Firebase Auth

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { getErrorMessage } from '../lib/firebase-errors';
import { pendingSignInContinueUrl } from '../lib/auth/pendingSignIn';
import { FormField, FormButton, FormError } from '../components/form';
import { z } from 'zod';

// Validation schema for forgot password form
const forgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
});

type ForgotPasswordData = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordPage() {
  // Carried onto both links back to sign-in, so a relying-party sign-in in
  // progress survives a detour through password reset (#1831).
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  /**
   * Whether the email we actually sent carries the pending sign-in (#1831).
   *
   * NOT the same question as "is a sign-in pending". If the continue URL is
   * refused because the app domain is missing from Firebase Auth's authorized
   * domains, we fall back to a plain email — and the advice below, which names
   * a Continue button, would then be describing a button that does not exist.
   * Gating on what was sent rather than on what could have been sent is what
   * stops the interface hiding that from the person reading it.
   */
  const [carriedPendingSignIn, setCarriedPendingSignIn] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<ForgotPasswordData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordData) => {
    try {
      setIsLoading(true);
      setError(null);

      // Carry a pending relying-party sign-in into the email, so setting a new
      // password leads back into the flow instead of to a dead end on our
      // domain (#1831). Absent when there is nothing pending, which is the
      // ordinary case.
      const continueUrl = pendingSignInContinueUrl(location.state);
      try {
        await sendPasswordResetEmail(
          auth,
          data.email,
          continueUrl ? { url: continueUrl } : undefined
        );
        setCarriedPendingSignIn(continueUrl !== undefined);
      } catch (sendError) {
        // The continue URL's domain must be on Firebase Auth's authorized
        // domains list, which is a MANUAL step and is recorded as done only in
        // development (`docs/setup/per-environment-manual-steps.md`, the
        // authorized-domains row). Where it has not been done, passing a
        // continue URL fails the send outright — so without this, adding the
        // URL would take password reset from working to not working at all in
        // staging and production, reported to the user as a generic error.
        //
        // The register row is the real fix. This bounds the blast radius while
        // it is outstanding: the reset email still goes, and the user recovers
        // through the tab they started in, exactly as they did before #1831.
        if (
          continueUrl &&
          (sendError as { code?: string } | null)?.code ===
            'auth/unauthorized-continue-uri'
        ) {
          console.warn(
            '[auth] Continue URL refused; sending a plain reset email. The app ' +
              'domain is probably missing from Firebase Auth authorized domains.',
            sendError
          );
          await sendPasswordResetEmail(auth, data.email);
          // Left false, deliberately. The advisory below names a Continue
          // button that this email does NOT have — telling somebody to look
          // for it would send them hunting on a page that has no such button,
          // and would hide the misconfiguration behind a confident
          // instruction. They still recover through the original tab, which is
          // what the remaining copy already tells them.
          setCarriedPendingSignIn(false);
        } else {
          throw sendError;
        }
      }
      setSuccess(true);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 leading-tight">
              Check Your Email
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              We've sent password reset instructions to
            </p>
            <p className="text-sm font-medium text-gray-900">
              {getValues('email')}
            </p>
            {/* Shown only when a relying-party sign-in is waiting (#1831). The
                link opens a NEW tab, and Firebase's own page does not send them
                onwards by itself — it stops at "Password changed" with a
                Continue button, which people miss. Naming that button is the
                point of this paragraph.

                "Leave this tab open" is the other half: the original tab is the
                reliable way back if anything goes wrong, or if they read the
                mail on a different device, where the Continue button lands them
                on a phone that never started the sign-in. */}
            {carriedPendingSignIn && (
              <p className="mt-4 text-sm text-gray-600">
                Leave this tab open. The link opens in a new tab — set a new
                password there, then choose <strong>Continue</strong>. You will
                be asked to sign in with your new password, and then returned to
                the application you came from. If anything goes wrong, come back
                to this tab and sign in here.
              </p>
            )}
          </div>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10 border border-gray-200">
            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                <p className="mb-2">
                  If you don't see the email in your inbox, please check your
                  spam folder.
                </p>
                <p>
                  The reset link will expire in 1 hour for security reasons.
                </p>
              </div>

              <div className="flex flex-col space-y-3">
                <Link
                  to="/"
                  state={location.state}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 transition-colors duration-200"
                >
                  Return to Sign In
                </Link>
                <button
                  onClick={() => setSuccess(false)}
                  className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 transition-colors duration-200"
                >
                  Try Different Email
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 leading-tight">
            Reset Your Password
          </h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600">
            Enter your email address and we'll send you a link to reset your
            password
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10 border border-gray-200">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {error && <FormError>{error}</FormError>}

            <FormField
              {...register('email')}
              label="Email Address"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="Enter your email address"
              error={errors.email?.message}
              helpText="We'll send password reset instructions to this email"
              isLoading={isLoading}
            />

            <FormButton
              type="submit"
              isLoading={isLoading}
              loadingText="Sending Reset Email..."
            >
              Send Reset Email
            </FormButton>
          </form>

          <div className="mt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">
                Remember your password?{' '}
                <Link
                  to="/"
                  // On the form itself, so this is the more travelled of the
                  // two: someone diverted here who then remembers their
                  // password takes it without ever submitting.
                  state={location.state}
                  className="font-medium text-sky-600 hover:text-sky-500 transition-colors duration-200"
                >
                  Sign in here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
